import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Drawer,
  IconButton,
  Tooltip,
  Button,
  Fab,
  Zoom,
  Grid,
  Card,
  CardContent,
  CardActionArea
} from '@mui/material';
import {
  Menu as MenuIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
  ChevronRight as ChevronRightIcon,
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import pdfService, { PDFDocument, PDFSection } from '../../services/pdfService';
import EditableSection from '../../components/pdf/EditableSection';

const ODOS_PWS_ID = '690a5c81f0111935eddfec29'; // The ID from our loaded PDF

const ODOSPWSViewer: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfDoc, setPdfDoc] = useState<PDFDocument | null>(null);
  const [sections, setSections] = useState<PDFSection[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [viewMode, setViewMode] = useState<'overview' | 'detailed'>('overview');

  useEffect(() => {
    loadPDF();
    
    // Handle scroll for back to top button
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadPDF = async () => {
    try {
      setLoading(true);
      setError(null);
      const doc = await pdfService.getPDFById(ODOS_PWS_ID);
      setPdfDoc(doc);
      
      // Get sections from document (already parsed and stored in database)
      const docSections = pdfService.getSections(doc);
      setSections(docSections);
      
      console.log(`Loaded ${docSections.length} sections from PDF`);
    } catch (err: any) {
      console.error('Error loading PDF:', err);
      setError(err.response?.data?.message || 'Failed to load PDF document');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSection = async (sectionId: string, newContent: string) => {
    if (!pdfDoc) return;

    try {
      setSaving(true);
      
      // Update the section in state
      const updatedSections = pdfService.updateSection(sections, sectionId, newContent);
      
      // Reconstruct full text
      const fullText = pdfService.reconstructText(updatedSections);
      
      // Save to backend
      const updatedDoc = await pdfService.updatePDFText(pdfDoc._id, fullText);
      
      // Update local state
      setPdfDoc(updatedDoc);
      setSections(updatedSections);
      setEditingSection(null);
      
      console.log('Section saved successfully');
    } catch (err: any) {
      console.error('Error saving section:', err);
      throw new Error(err.response?.data?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    if (pdfDoc) {
      pdfService.downloadPDF(pdfDoc._id);
    }
  };

  const scrollToSection = (sectionId: string) => {
    setSelectedSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setDrawerOpen(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2, color: 'primary.main' }}>
          Loading ODOS IV PWS Document...
        </Typography>
      </Container>
    );
  }

  if (error || !pdfDoc) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          {error || 'Document not found'}
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/odos')}
          sx={{ mt: 2, bgcolor: 'primary.main' }}
        >
          Back to ODOS Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Navigation Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 320,
            boxSizing: 'border-box',
            pt: 8
          }
        }}
      >
        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Merriweather, serif' }}>
            Table of Contents
          </Typography>
          <Typography variant="caption">
            {sections.length} sections
          </Typography>
        </Box>
        <List>
          {sections.map((section) => (
            <ListItem key={section.id} disablePadding>
              <ListItemButton
                onClick={() => scrollToSection(section.id)}
                selected={selectedSection === section.id}
                sx={{
                  pl: (section.sectionNumber.split('.').length) * 2,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(0, 51, 102, 0.1)',
                    borderLeft: '4px solid #003366'
                  }
                }}
              >
                <ListItemText
                  primary={`${section.sectionNumber}. ${section.title}`}
                  primaryTypographyProps={{
                    fontSize: section.sectionNumber.includes('.') ? '0.85rem' : '0.95rem',
                    fontWeight: section.sectionNumber.includes('.') ? 'normal' : 'bold'
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1 }}>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          {/* Header */}
          <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{ color: 'white', mr: 2 }}
                aria-label="Open table of contents"
              >
                <MenuIcon />
              </IconButton>
              <DescriptionIcon sx={{ fontSize: 40, mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontFamily: 'Merriweather, serif', fontWeight: 'bold' }}>
                  {pdfDoc.title}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                  {pdfDoc.description}
                </Typography>
              </Box>
              <Tooltip title="Download Original PDF">
                <IconButton
                  onClick={handleDownload}
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': { bgcolor: '#f0f0f0' }
                  }}
                  aria-label="Download PDF"
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Breadcrumbs */}
            <Breadcrumbs
              separator={<ChevronRightIcon fontSize="small" sx={{ color: 'white' }} />}
              sx={{ color: 'white' }}
            >
              <Link
                color="inherit"
                href="/odos"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/odos');
                }}
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              >
                ODOS Dashboard
              </Link>
              <Typography color="inherit">PWS Document</Typography>
            </Breadcrumbs>

            {/* Metadata */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              <Chip
                label={`${pdfDoc.pageCount} pages`}
                size="small"
                sx={{ bgcolor: 'white', color: 'primary.main' }}
              />
              <Chip
                label={`${pdfDoc.fileSizeMB || (pdfDoc.fileSize / (1024 * 1024)).toFixed(2)} MB`}
                size="small"
                sx={{ bgcolor: 'white', color: 'primary.main' }}
              />
              <Chip
                label={`${sections.length} sections`}
                size="small"
                sx={{ bgcolor: 'white', color: 'primary.main' }}
              />
              {pdfDoc.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white' }}
                />
              ))}
            </Box>
          </Paper>

          {/* Navigation Bar */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/odos')}
              sx={{
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(0, 51, 102, 0.05)'
                }
              }}
            >
              Back to ODOS Dashboard
            </Button>
            
            <Divider orientation="vertical" flexItem />
            
            <Button
              variant="outlined"
              startIcon={<MenuIcon />}
              onClick={() => setDrawerOpen(true)}
              sx={{
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(0, 51, 102, 0.05)'
                }
              }}
            >
              Table of Contents ({sections.length} sections)
            </Button>
            
            <Button
              variant="contained"
              onClick={() => {
                setViewMode('detailed');
                setTimeout(() => scrollToSection('section-1'), 100);
              }}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }}
            >
              Jump to Section 1: OVERVIEW
            </Button>
            
            <Box sx={{ flex: 1 }} />
            
            <Button
              variant={viewMode === 'overview' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('overview')}
              sx={{
                bgcolor: viewMode === 'overview' ? '#2E7D32' : 'transparent',
                borderColor: '#2E7D32',
                color: viewMode === 'overview' ? 'white' : '#2E7D32',
                '&:hover': {
                  bgcolor: viewMode === 'overview' ? '#1B5E20' : 'rgba(46, 125, 50, 0.05)'
                }
              }}
            >
              Section Overview
            </Button>
            
            <Button
              variant={viewMode === 'detailed' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('detailed')}
              sx={{
                bgcolor: viewMode === 'detailed' ? '#2E7D32' : 'transparent',
                borderColor: '#2E7D32',
                color: viewMode === 'detailed' ? 'white' : '#2E7D32',
                '&:hover': {
                  bgcolor: viewMode === 'detailed' ? '#1B5E20' : 'rgba(46, 125, 50, 0.05)'
                }
              }}
            >
              Detailed View
            </Button>
            
            <Box sx={{ flex: 1 }} />
            
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{
                borderColor: 'secondary.main',
                color: 'secondary.main',
                '&:hover': {
                  borderColor: 'secondary.main',
                  bgcolor: 'rgba(179, 27, 27, 0.05)'
                }
              }}
            >
              Download PDF
            </Button>
          </Paper>

          {/* Instructions */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {viewMode === 'overview' ? '📋 Section Overview Mode' : '📝 Detailed Edit Mode'}
            </Typography>
            <Typography variant="body2">
              {viewMode === 'overview' ? (
                <>
                  • Click any section card to view and edit its content<br />
                  • Use "Detailed View" button to see all sections at once<br />
                  • {sections.length} sections available
                </>
              ) : (
                <>
                  • Click on any section card to start editing<br />
                  • Press <strong>Ctrl+Enter</strong> (or <strong>Cmd+Enter</strong>) to save<br />
                  • Press <strong>Esc</strong> to cancel<br />
                  • Changes are saved to the database automatically
                </>
              )}
            </Typography>
          </Alert>

          {/* Saving Indicator */}
          {saving && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={20} />
                <Typography>Saving changes...</Typography>
              </Box>
            </Alert>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* Section Overview Grid */}
          {viewMode === 'overview' && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {sections.map((section) => (
                <Grid item xs={12} sm={6} md={4} key={section.id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      '&:hover': { 
                        boxShadow: 6,
                        transform: 'translateY(-4px)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                  >
                    <CardActionArea 
                      onClick={() => {
                        setViewMode('detailed');
                        setTimeout(() => scrollToSection(section.id), 100);
                      }}
                      sx={{ height: '100%' }}
                    >
                      <CardContent>
                        <Typography 
                          variant="h6" 
                          gutterBottom 
                          sx={{ 
                            color: 'primary.main',
                            fontFamily: 'Merriweather, serif',
                            fontWeight: 'bold',
                            mb: 2
                          }}
                        >
                          Section {section.sectionNumber}
                        </Typography>
                        <Typography 
                          variant="subtitle1" 
                          gutterBottom
                          sx={{ 
                            fontWeight: 500,
                            mb: 2,
                            minHeight: '3em'
                          }}
                        >
                          {section.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          {section.metadata && (
                            <>
                              <Chip
                                label={`${section.metadata.characterCount.toLocaleString()} chars`}
                                size="small"
                                sx={{ bgcolor: '#f0f0f0', fontSize: '0.7rem' }}
                              />
                              <Chip
                                label={`${section.metadata.lineCount} lines`}
                                size="small"
                                sx={{ bgcolor: '#f0f0f0', fontSize: '0.7rem' }}
                              />
                            </>
                          )}
                        </Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            mt: 2,
                            fontStyle: 'italic'
                          }}
                        >
                          Click to view and edit →
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Detailed Sections View */}
          {viewMode === 'detailed' && (
            <>
              {sections.map((section) => (
                <Box key={section.id} id={section.id}>
                  <EditableSection
                    section={section}
                    onSave={handleSaveSection}
                    isEditing={editingSection === section.id}
                    onEditStart={(id) => setEditingSection(id)}
                    onEditCancel={() => setEditingSection(null)}
                  />
                </Box>
              ))}
            </>
          )}

          {/* Footer */}
          <Paper elevation={1} sx={{ p: 2, mt: 4, bgcolor: '#f5f5f5', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Last updated: {new Date(pdfDoc.updatedAt).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Document ID: {pdfDoc._id}
            </Typography>
          </Paper>
        </Container>
      </Box>

      {/* Floating Back to Top Button */}
      <Zoom in={showBackToTop}>
        <Fab
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            bgcolor: 'primary.main',
            color: 'white',
            '&:hover': {
              bgcolor: 'primary.dark'
            }
          }}
          aria-label="Back to top"
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>
    </Box>
  );
};

export default ODOSPWSViewer;
